import { CHROME_ROUTES } from '@/components/chrome/chrome-links';

/**
 * Canonical marketing-site origin (Phase 7 v1.1 iter 306).
 *
 * Single source of truth for `https://syncanix.com` across every
 * build-time generator that emits feed URLs or absolute href values:
 *
 *   - `apps/marketing/src/features/trust/content/build-rss.ts`
 *     (iter 244 — sub-processor RSS)
 *   - `apps/marketing/src/features/seo/content/build-sitemap.ts`
 *     (iter 296 — sitemap.xml)
 *
 * Before iter 306 each generator declared its own copy of the
 * string. iter-271's doc-comment explicitly flagged the duplication
 * as a v1.1 polish item. Consolidating here closes that note.
 *
 * **Trailing slash convention** (Lead Dev hat, locked): the constant
 * does NOT include a trailing slash, so callers always compose with
 * `${SITE_BASE_URL}/<path>` and the home page renders as
 * `https://syncanix.com` (no trailing slash) in feed URLs. Matches
 * iter-244 + iter-271 + iter-296 emitted output exactly so the
 * consolidation is byte-equivalent.
 *
 * **No env-var injection yet** (Architect hat, locked): the
 * marketing site is purely static and deployed to Amplify; no
 * runtime env-var loader. When/if the origin moves (e.g.
 * `https://www.syncanix.com` vs `https://syncanix.com` resolution),
 * this file changes in the same commit as the deploy config.
 */
export const SITE_BASE_URL = 'https://syncanix.com';

/**
 * Canonical marketing→app link (Phase 9 Pass 1 — AF-420 / AF-426).
 *
 * The authenticated product lives at a *different* origin from the
 * static marketing site (`app.<domain>`), so — unlike `SITE_BASE_URL`
 * (the marketing site's own origin, genuinely static) — the app origin
 * must be injectable per environment. This mirrors the DSAR form's
 * `VITE_SYNCANIX_API_URL` precedent: Vite inlines `import.meta.env`
 * at build time for the static bundle.
 *
 * This is the SINGLE place the app origin is named. Components compose
 * their "start free / start trial / get started" CTAs from
 * `appSignupUrl()` so no `app.syncanix.com` literal is scattered
 * across the component tree (AF-426). When the front-door form lands in
 * Pass 3 (AF-438), the sales destination upgrades from the interim
 * `mailto:` to that form — again, in this one file.
 */
const DEFAULT_APP_BASE_URL = 'https://app.syncanix.com';

/** The app origin, env-driven, trailing slash stripped for clean composition. */
function appBaseUrl(): string {
  const value = import.meta.env.VITE_APP_URL as string | undefined;
  const resolved = typeof value === 'string' && value.length > 0 ? value : DEFAULT_APP_BASE_URL;
  return resolved.replace(/\/+$/, '');
}

/**
 * The one real destination every marketing sign-up CTA resolves to:
 * the app's Auth0 sign-up flow. `intent=signup` lets the app route a
 * marketing-sourced visitor straight into account creation.
 */
export function appSignupUrl(): string {
  return `${appBaseUrl()}/signin?intent=signup`;
}

/**
 * The marketing→app *sign-in* link for visitors who already have an
 * account. Same `/signin` entry point as {@link appSignupUrl}, but
 * WITHOUT `intent=signup` — so the app forwards Auth0's login screen
 * instead of the account-creation screen. Composed from the same
 * env-driven origin so the AF-426 single-source guard holds.
 */
export function appLoginUrl(): string {
  return `${appBaseUrl()}/signin`;
}

/**
 * The marketing→app *public scan* link — the top-of-funnel "try it in 60s,
 * no signup" entry (CS-262c). Points at the app origin's `/scan`, a PUBLIC
 * unauthenticated route (routes.tsx — A24/AF-133), so a marketing visitor
 * lands straight on the scan tool without an Auth0 bounce. Same env-driven
 * origin as the sign-in CTAs, so the AF-426 single-source guard holds.
 */
export function appScanUrl(): string {
  return `${appBaseUrl()}/scan`;
}

/**
 * "Talk to sales" destination — the `/contact` form (B25, the AF-438
 * upgrade this seam was built for). The published address is exported for
 * fallback/mailto surfaces.
 *
 * Operator directive 2026-07-16: sales enquiries now route to the dedicated
 * `sales@syncanix.com` mailbox (kept in sync with `CONTACT_ADDRESSES.sales`).
 *
 * The route itself comes from `CHROME_ROUTES.contact` — the canonical route
 * registry — rather than a second `/contact` literal, so a route rename can
 * never leave this helper pointing at a page that moved.
 */
export const SALES_CONTACT_EMAIL = 'sales@syncanix.com';

export function salesContactHref(): string {
  return CHROME_ROUTES.contact;
}
